/**
 * Reseller Portal — iteration 35
 * Lives on `resellers.cyannect.com` (or `?host=reseller` in preview).
 * Includes: application, status, catalog browser (opt-in merchant offers only),
 * tracking-link generator, and an analytics dashboard (clicks, conversions,
 * GMV, commission earned) with 7d/30d/90d range toggles.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Sparkles, ShieldCheck, TrendingUp, Link2,
  CheckCircle2, Clock, XCircle, Store, ArrowRight, Copy,
  MousePointerClick, ShoppingBag, DollarSign, Percent, Wallet, LineChart,
  CalendarClock, Receipt, Info, Download,
} from "lucide-react";
import Seo from "@/components/Seo";

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800 border-amber-300",
  approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  suspended: "bg-slate-100 text-slate-700 border-slate-300",
  rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

function StatusPill({ status }) {
  const icons = { pending: Clock, approved: CheckCircle2, suspended: XCircle, rejected: XCircle };
  const Icon = icons[status] || Clock;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status] || STATUS_COLORS.pending}`}
          data-testid={`reseller-status-pill-${status}`}>
      <Icon className="w-3.5 h-3.5" /> {status}
    </span>
  );
}

/* ---------- Application form (unauth-safe view) ---------- */

function ResellerApplyForm({ onSubmitted }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    business_name: "",
    website: "",
    audience_size: "",
    verticals: "",
    traffic_sources: "",
    notes: "",
  });

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        business_name: form.business_name.trim(),
        website: form.website.trim() || undefined,
        audience_size: form.audience_size ? Number(form.audience_size) : undefined,
        verticals: form.verticals.split(",").map(v => v.trim()).filter(Boolean),
        traffic_sources: form.traffic_sources.split(",").map(v => v.trim()).filter(Boolean),
        notes: form.notes.trim() || undefined,
      };
      const { data } = await api.post("/reseller/apply", body);
      toast.success("Application received — we'll review it within 48 hours.");
      onSubmitted(data);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.error === "already_applied") {
        toast.info("You've already applied — showing your current status.");
        onSubmitted({ status: d.status });
      } else {
        toast.error(typeof d === "string" ? d : (d?.message || "Failed to submit"));
      }
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="max-w-2xl mx-auto p-8 rounded-2xl bg-white border border-slate-200 shadow-sm" data-testid="reseller-apply-form">
      <div className="inline-flex items-center gap-2 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold mb-2">
        <Sparkles className="w-3.5 h-3.5" /> Reseller application
      </div>
      <h2 className="text-2xl font-display text-slate-900">Apply to distribute Cyannect offers</h2>
      <p className="text-sm text-slate-500 mt-1 mb-6">Tell us about your audience. Approvals typically land within 48 hours.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Business / brand name *</label>
          <input required value={form.business_name} onChange={e => setForm({ ...form, business_name: e.target.value })}
                 className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-cyan-500 outline-none"
                 data-testid="reseller-business-name" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Website</label>
          <input type="url" value={form.website} onChange={e => setForm({ ...form, website: e.target.value })} placeholder="https://…"
                 className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-cyan-500 outline-none font-mono"
                 data-testid="reseller-website" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Audience size</label>
          <input type="number" min={0} value={form.audience_size} onChange={e => setForm({ ...form, audience_size: e.target.value })} placeholder="e.g. 50000"
                 className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-cyan-500 outline-none"
                 data-testid="reseller-audience-size" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Verticals</label>
          <input value={form.verticals} onChange={e => setForm({ ...form, verticals: e.target.value })} placeholder="fashion, beauty, home"
                 className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-cyan-500 outline-none"
                 data-testid="reseller-verticals" />
        </div>
        <div>
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Traffic sources</label>
          <input value={form.traffic_sources} onChange={e => setForm({ ...form, traffic_sources: e.target.value })} placeholder="blog, newsletter, tiktok"
                 className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-cyan-500 outline-none"
                 data-testid="reseller-traffic-sources" />
        </div>
        <div className="col-span-2">
          <label className="block text-[10px] uppercase tracking-widest text-slate-500 mb-1">Notes for the review team</label>
          <textarea rows={3} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                    className="w-full px-3 py-2 text-sm rounded-lg border border-slate-300 bg-white focus:border-cyan-500 outline-none"
                    data-testid="reseller-notes" />
        </div>
      </div>
      <button type="submit" disabled={busy} className="mt-6 w-full py-3 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-40"
              data-testid="reseller-apply-submit">
        {busy ? "Submitting…" : (<>Submit application <ArrowRight className="w-4 h-4" /></>)}
      </button>
    </form>
  );
}

/* ---------- Status / dashboard ---------- */

function ResellerStatus({ profile }) {
  return (
    <div className="max-w-3xl mx-auto p-8 rounded-2xl bg-white border border-slate-200" data-testid="reseller-status">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold">Application status</div>
          <h2 className="text-2xl font-display text-slate-900">{profile.business_name || "Your reseller account"}</h2>
        </div>
        <StatusPill status={profile.status} />
      </div>
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div><dt className="text-slate-500 text-xs uppercase tracking-widest">Commission rate</dt><dd className="text-slate-900 font-medium">{((profile.commission_rate ?? 0) * 100).toFixed(0)}%</dd></div>
        <div><dt className="text-slate-500 text-xs uppercase tracking-widest">Applied</dt><dd className="text-slate-900 font-medium">{profile.created_at ? new Date(profile.created_at).toLocaleDateString() : "—"}</dd></div>
        {profile.website && <div><dt className="text-slate-500 text-xs uppercase tracking-widest">Website</dt><dd className="text-slate-900 font-mono text-xs truncate">{profile.website}</dd></div>}
        {profile.audience_size != null && <div><dt className="text-slate-500 text-xs uppercase tracking-widest">Audience</dt><dd className="text-slate-900 font-medium">{Number(profile.audience_size).toLocaleString()}</dd></div>}
      </dl>
      {profile.status === "pending" && (
        <div className="mt-6 p-4 rounded-lg bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="reseller-pending-hint">
          We typically review within 48 hours. You'll get an in-app notification + email when a decision is made.
        </div>
      )}
      {profile.status === "rejected" && profile.rejection_reason && (
        <div className="mt-6 p-4 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-900">
          <strong>Reason:</strong> {profile.rejection_reason}
        </div>
      )}
    </div>
  );
}

/* ---------- Analytics dashboard ---------- */

function PayoutPreviewCard({ preview, commissionRate }) {
  if (!preview) return null;
  const nextDate = preview.next_payout_date ? new Date(preview.next_payout_date) : null;
  const fmtMoney = (n) => cad(n || 0);
  const nextLabel = nextDate ? nextDate.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }) : "next payout cycle";
  const commissionPct = ((commissionRate ?? 0.4) * 100).toFixed(0);
  return (
    <div className="mt-6 p-6 rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-cyan-50 border border-emerald-100 relative overflow-hidden"
         data-testid="reseller-payout-preview">
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-emerald-200/40 rounded-full blur-3xl" />
      <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="inline-flex items-center gap-1.5 text-emerald-700 text-[10px] uppercase tracking-widest font-semibold">
            <CalendarClock className="w-3.5 h-3.5" /> Next payout · {nextLabel}
          </div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="text-3xl font-display text-slate-900 tabular-nums" data-testid="reseller-payout-total">
              {fmtMoney(preview.mtd_commission)}
            </span>
            <span className="text-xs text-slate-500">estimated · month-to-date</span>
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Based on {Number(preview.mtd_conversions || 0).toLocaleString()} attributed
            {preview.mtd_conversions === 1 ? " conversion" : " conversions"} · {commissionPct}% of {fmtMoney(preview.mtd_performance_fees)} performance fees.
          </div>
        </div>
        <div className="shrink-0 flex items-center gap-2">
          <div className="p-3 rounded-2xl bg-emerald-500/10 border border-emerald-200/60">
            <Wallet className="w-5 h-5 text-emerald-700" />
          </div>
          <div className="text-[10px] text-slate-400 max-w-[180px]">
            Payouts settle on the 1st of the following month. Refunds voided during the window are automatically netted.
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiTile({ icon: Icon, label, value, sublabel, tone = "cyan", testid }) {
  const tones = {
    cyan: "text-cyan-700 bg-cyan-50 border-cyan-100",
    emerald: "text-emerald-700 bg-emerald-50 border-emerald-100",
    indigo: "text-indigo-700 bg-indigo-50 border-indigo-100",
    amber: "text-amber-700 bg-amber-50 border-amber-100",
    rose: "text-rose-700 bg-rose-50 border-rose-100",
    slate: "text-slate-700 bg-slate-50 border-slate-200",
  };
  return (
    <div className="p-5 rounded-2xl bg-white border border-slate-200" data-testid={testid}>
      <div className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border ${tones[tone]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
      <div className="text-2xl font-display text-slate-900 mt-1" data-testid={`${testid}-value`}>{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
}

/* ---------- Payout ledger (settlement history) ---------- */

const STATUS_BADGE = {
  paid:     { tone: "bg-emerald-100 text-emerald-800 border-emerald-200", label: "Paid" },
  upcoming: { tone: "bg-cyan-100 text-cyan-800 border-cyan-200",           label: "Upcoming" },
  voided:   { tone: "bg-rose-100 text-rose-800 border-rose-200",           label: "Voided" },
  empty:    { tone: "bg-slate-100 text-slate-500 border-slate-200",        label: "No activity" },
};

function StatusBadge({ status, testid }) {
  const s = STATUS_BADGE[status] || STATUS_BADGE.empty;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${s.tone}`}
          data-testid={testid}>
      {s.label}
    </span>
  );
}

function fmtPeriod(period) {
  // "2026-08" → "Aug 2026"
  if (!period) return "—";
  const [y, m] = period.split("-").map(Number);
  const d = new Date(Date.UTC(y, (m || 1) - 1, 1));
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric", timeZone: "UTC" });
}

function PayoutLedger() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get("/reseller/payouts?months=12")
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const fmtMoney = (n) => cad(n || 0);

  function downloadCsv() {
    if (!data?.items?.length) return;
    const headers = ["Period", "Conversions", "Voided", "GMV", "Performance Fees", "Commission Rate", "Commission", "Status", "Settled At", "Voided Reason"];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [headers.join(",")];
    for (const row of data.items) {
      lines.push([
        row.period,
        row.conversions,
        row.voided,
        row.gmv,
        row.performance_fees,
        row.commission_rate,
        row.commission,
        row.status,
        row.paid_at || "",
        row.voided_reason || "",
      ].map(escape).join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `cyannect-payout-ledger-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 0);
  }

  return (
    <div className="mt-6 rounded-2xl bg-white border border-slate-200 overflow-hidden" data-testid="reseller-payout-ledger">
      <div className="px-6 py-5 border-b border-slate-200 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold">
            <Receipt className="w-3.5 h-3.5" /> Payout ledger
          </div>
          <h3 className="text-xl font-display text-slate-900 mt-1">Settlement history</h3>
          <p className="text-sm text-slate-500 mt-1">
            Every month, closed. Refunds voided during a period reduce that month's commission.
          </p>
        </div>
        <div className="flex items-end gap-4 shrink-0">
          {data?.totals && (
            <>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Paid to date</div>
                <div className="text-lg font-display text-emerald-700 tabular-nums" data-testid="reseller-payout-total-paid">
                  {fmtMoney(data.totals.paid_to_date)}
                </div>
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Upcoming</div>
                <div className="text-lg font-display text-cyan-700 tabular-nums" data-testid="reseller-payout-total-upcoming">
                  {fmtMoney(data.totals.upcoming)}
                </div>
              </div>
            </>
          )}
          <button
            onClick={downloadCsv}
            disabled={!data?.items?.length}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-slate-900 hover:bg-slate-800 text-white disabled:opacity-40"
            data-testid="reseller-payout-download-csv"
            title="Download ledger as CSV"
          >
            <Download className="w-3.5 h-3.5" /> CSV
          </button>
        </div>
      </div>

      {data?.sandbox && (
        <div className="px-6 py-2.5 text-[11px] text-amber-800 bg-amber-50 border-b border-amber-100 flex items-center gap-2"
             data-testid="reseller-payout-sandbox-note">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Sandbox mode — Stripe Connect payouts wire up automatically once your platform Connect keys are live. Numbers are computed from confirmed conversions.
        </div>
      )}

      {loading && <div className="p-8 text-center text-sm text-slate-400 italic">Loading ledger…</div>}

      {!loading && data?.items?.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-5 py-3 font-semibold">Period</th>
                <th className="px-5 py-3 font-semibold text-right">Conversions</th>
                <th className="px-5 py-3 font-semibold text-right">GMV</th>
                <th className="px-5 py-3 font-semibold text-right">Fees</th>
                <th className="px-5 py-3 font-semibold text-right">Commission</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">Settled</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.items.map((row, i) => (
                <tr key={row.period} data-testid={`reseller-payout-row-${row.period}`}
                    className={row.status === "empty" ? "text-slate-400" : ""}>
                  <td className="px-5 py-3 font-medium text-slate-900">{fmtPeriod(row.period)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums">
                    {Number(row.conversions || 0).toLocaleString()}
                    {row.voided > 0 && (
                      <span className="ml-1 text-[10px] text-rose-500" title={`${row.voided} voided in-period`}>
                        (−{row.voided})
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-600">{fmtMoney(row.gmv)}</td>
                  <td className="px-5 py-3 text-right font-mono tabular-nums text-slate-600">{fmtMoney(row.performance_fees)}</td>
                  <td className={`px-5 py-3 text-right font-mono tabular-nums font-semibold ${
                    row.status === "paid" ? "text-emerald-700" :
                    row.status === "upcoming" ? "text-cyan-700" :
                    row.status === "voided" ? "text-rose-700" : "text-slate-400"
                  }`}>
                    {fmtMoney(row.commission)}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={row.status} testid={`reseller-payout-status-${row.period}`} />
                  </td>
                  <td className="px-5 py-3 text-xs text-slate-500">
                    {row.paid_at ? new Date(row.paid_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!loading && (!data?.items || data.items.length === 0) && (
        <div className="p-8 text-center text-sm text-slate-400 italic" data-testid="reseller-payout-ledger-empty">
          No payout history yet — your first month lands here after the 1st.
        </div>
      )}
    </div>
  );
}

function ResellerAnalytics() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/reseller/analytics/overview?range=${range}`)
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const fmtMoney = (n) => cad(n || 0);
  const fmtInt = (n) => Number(n || 0).toLocaleString();

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="reseller-analytics">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold flex items-center gap-1.5">
            <LineChart className="w-3.5 h-3.5" /> Performance
          </div>
          <h2 className="text-2xl font-display text-slate-900">Analytics overview</h2>
          <p className="text-sm text-slate-500">Attribution snapshot for the selected window.</p>
        </div>
        <div className="inline-flex items-center rounded-full bg-slate-100 p-1" data-testid="reseller-range-toggle">
          {["7d", "30d", "90d"].map(r => (
            <button key={r} onClick={() => setRange(r)}
                    data-testid={`reseller-range-${r}`}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-full transition ${range === r ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
              {r.toUpperCase()}
            </button>
          ))}
        </div>
      </div>

      {loading && <div className="text-sm text-slate-400 italic text-center py-12">Loading analytics…</div>}
      {!loading && !data && (
        <div className="text-sm text-slate-400 italic text-center py-12" data-testid="reseller-analytics-empty">
          No analytics available yet.
        </div>
      )}
      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KpiTile icon={MousePointerClick} label="Clicks" value={fmtInt(data.clicks)} tone="cyan" testid="reseller-kpi-clicks" />
            <KpiTile icon={ShoppingBag} label="Conversions" value={fmtInt(data.conversions)} tone="indigo" testid="reseller-kpi-conversions" />
            <KpiTile icon={Percent} label="Conv. rate" value={`${(data.conversion_rate ?? 0).toFixed(2)}%`} tone="amber" testid="reseller-kpi-cvr" />
            <KpiTile icon={DollarSign} label="Attributed GMV" value={fmtMoney(data.gmv)} tone="emerald" testid="reseller-kpi-gmv" />
            <KpiTile icon={TrendingUp} label="Performance fees" value={fmtMoney(data.performance_fees)} tone="slate" testid="reseller-kpi-fees" />
            <KpiTile icon={Wallet} label="Commission earned"
                     sublabel={`Rate: ${((data.commission_rate ?? 0) * 100).toFixed(0)}%`}
                     value={fmtMoney(data.commission_earned)} tone="rose" testid="reseller-kpi-commission" />
          </div>
          {data.payout_preview && <PayoutPreviewCard preview={data.payout_preview} commissionRate={data.commission_rate} />}
          <PayoutLedger />
        </>
      )}
    </div>
  );
}

/* ---------- Catalog + tracking-link generator (approved resellers only) ---------- */

function ResellerCatalog({ profile }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get("/reseller/catalog?limit=24")
      .then(r => setItems(r.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const trackingUrl = (c) => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const slug = c.slug || c.id;
    return `${origin}/r/aff/${profile.id || "your-slug"}/${slug}`;
  };

  const discountLabel = (c) => {
    if (c.discount_type === "percent" && c.discount_value) return `${c.discount_value}% off`;
    if (c.discount_type === "amount" && c.discount_value) return `CA$${c.discount_value} off`;
    if (c.discount_value) return `${c.discount_value} off`;
    return null;
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="reseller-catalog">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-cyan-700 font-semibold flex items-center gap-1.5">
            <Store className="w-3.5 h-3.5" /> Catalog
          </div>
          <h2 className="text-2xl font-display text-slate-900">Merchant-approved offers</h2>
          <p className="text-sm text-slate-500">Copy a tracking link and drop it into your content. Every click, conversion, and dollar is attributed to you.</p>
        </div>
      </div>
      {loading && <div className="text-sm text-slate-400 italic text-center py-12">Loading catalog…</div>}
      {!loading && items.length === 0 && (
        <div className="text-sm text-slate-400 italic text-center py-12" data-testid="reseller-catalog-empty">
          No offers available yet — check back soon.
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map(c => {
          const label = discountLabel(c);
          return (
            <div key={c.id} className="p-4 rounded-2xl bg-white border border-slate-200 hover:shadow-md transition" data-testid={`reseller-offer-${c.id}`}>
              <div className="text-xs text-slate-500">{c.store?.name || c.store_name || "Merchant"}</div>
              <div className="text-sm font-medium text-slate-900 line-clamp-2 mt-1">{c.title || c.name}</div>
              {label && (
                <div className="mt-2 inline-flex items-center gap-1 text-emerald-700 text-xs font-semibold bg-emerald-50 px-2 py-0.5 rounded-full">
                  <TrendingUp className="w-3 h-3" /> {label}
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 border border-slate-200 px-2 py-1.5">
                <Link2 className="w-3 h-3 text-slate-400 shrink-0" />
                <code className="text-[10px] text-slate-600 font-mono truncate flex-1">{trackingUrl(c)}</code>
                <button onClick={() => { navigator.clipboard.writeText(trackingUrl(c)); setCopiedId(c.id); setTimeout(() => setCopiedId(null), 1200); }}
                        className="text-cyan-700 hover:text-cyan-900" data-testid={`reseller-copy-${c.id}`}>
                  {copiedId === c.id ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Page container ---------- */

export default function ResellerPortal() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notEnabled, setNotEnabled] = useState(false);
  const [tab, setTab] = useState("analytics"); // analytics | catalog

  const seo = (
    <Seo
      title="Reseller portal — earn commission promoting Canadian brands"
      description="Apply to the Cyannect reseller programme. Get tracked links, opt-in to merchant catalogs and earn commission on every verified sale. Transparent analytics, weekly payouts."
      path="/reseller"
      keywords="Cyannect reseller, affiliate programme Canada, coupon reseller, performance marketing"
    />
  );

  async function refresh() {
    try {
      const { data } = await api.get("/reseller/profile");
      setProfile(data);
    } catch (e) {
      if (e.response?.status === 404) {
        const d = e.response.data?.detail;
        if (d?.error === "not_a_reseller") setProfile(null);
        else if (typeof d === "string" && d.includes("not enabled")) setNotEnabled(true);
        else setNotEnabled(true);
      }
    } finally { setLoading(false); }
  }
  useEffect(() => { if (user) refresh(); else setLoading(false); }, [user]);

  if (!user) {
    return (
      <div className="max-w-md mx-auto p-8 text-center" data-testid="reseller-signin-prompt">
        {seo}
        <div className="inline-flex items-center gap-2 text-cyan-700 text-[10px] uppercase tracking-widest font-semibold mb-2">
          <Sparkles className="w-3.5 h-3.5" /> Reseller portal
        </div>
        <h1 className="text-2xl font-display text-slate-900">Sign in to apply or manage your reseller account</h1>
        <Link to="/login" className="mt-6 inline-block px-6 py-2.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-semibold">
          Sign in
        </Link>
      </div>
    );
  }
  if (loading) return <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>;
  if (notEnabled) return (
    <div className="max-w-lg mx-auto p-8 text-center" data-testid="reseller-disabled">
      <ShieldCheck className="w-8 h-8 text-slate-400 mx-auto mb-3" />
      <h2 className="text-xl font-display text-slate-900">Reseller applications aren't open yet</h2>
      <p className="text-sm text-slate-500 mt-2">We're rolling out to select partners first. Follow @cyannect for updates or email partners@cyannect.com to jump the queue.</p>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-4rem)] py-10 bg-gradient-to-b from-slate-50 to-white">
      {seo}
      <div className="max-w-6xl mx-auto px-6 mb-4">
        <h1 className="sr-only">Cyannect reseller portal</h1>
      </div>
      {!profile && <ResellerApplyForm onSubmitted={(p) => setProfile(p)} />}
      {profile && profile.status !== "approved" && <ResellerStatus profile={profile} />}
      {profile && profile.status === "approved" && (
        <>
          <ResellerStatus profile={profile} />
          <div className="max-w-6xl mx-auto px-6 mt-8">
            <div className="inline-flex items-center rounded-full bg-white border border-slate-200 p-1 shadow-sm" data-testid="reseller-tabs">
              <button onClick={() => setTab("analytics")} data-testid="reseller-tab-analytics"
                      className={`text-xs font-semibold px-4 py-2 rounded-full transition inline-flex items-center gap-1.5 ${tab === "analytics" ? "bg-cyan-500 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                <LineChart className="w-3.5 h-3.5" /> Analytics
              </button>
              <button onClick={() => setTab("catalog")} data-testid="reseller-tab-catalog"
                      className={`text-xs font-semibold px-4 py-2 rounded-full transition inline-flex items-center gap-1.5 ${tab === "catalog" ? "bg-cyan-500 text-slate-900" : "text-slate-500 hover:text-slate-700"}`}>
                <Store className="w-3.5 h-3.5" /> Catalog
              </button>
            </div>
          </div>
          {tab === "analytics" ? <ResellerAnalytics /> : <ResellerCatalog profile={profile} />}
        </>
      )}
    </div>
  );
}
